import type { Result } from '../types/result';
import type { SiteConfig } from '../types/config';
import { buildConversionCreationRequest } from './buildConversionCreationRequest';
import { initConversion } from './data-fetch/postLeadSubmission';

/**
 * Country/state geo carried into the click-time conversion create. Sourced
 * from the same `flowState.location` merge point `resolveGeolocation` /
 * `readInferredCountryCode` read (IP inference, overwritten by an answered
 * location question) — see {@link useHandleSubmit}'s `locationState` for the
 * submit-time counterpart this mirrors.
 */
export type EnsureConversionLocation = {
  country?: string;
  state?: string;
};

/**
 * Create the CMS v2 conversion ("APP_VIEWED") at the product click that routes
 * a user into an application view — pre-submit — restoring Supersonic parity
 * (Mobius fired `APP_VIEWED` at redirect/application-view time, before lead
 * submit). The submit path then reuses/completes this same conversion via
 * `productClickedCorrelationId` continuity rather than creating a second one.
 *
 * Guard: a conversion is only created when the result carries a usable offering
 * identity (`offeringId`, falling back to the forward `offeringUuid`) AND the
 * site has a `tenantId` — the two fields `buildConversionCreationRequest`
 * requires. Under-specified clicks (non-application read-more/compare, noop
 * recommender cards, DCS forwards) resolve to a no-op so they never mint a
 * conversion.
 *
 * `location` is forwarded to CMS as a body override so this click-time create
 * carries the same geo as the governed submit path, rather than falling back
 * to CMS's CloudFront viewer-country headers — which, for this server-to-server
 * BFF-proxied call, reflect the calling server's egress location, not the
 * visitor's. Omit when unresolved so CMS falls back to its header geo exactly
 * as before.
 *
 * Fail-safe: this must never throw into the click handler that has to route the
 * user into the application view. A failed create logs and degrades to "no
 * click-time conversion" (the governed submit path still creates one).
 *
 * @returns the created `conversionId`, or `undefined` when the create was
 *   skipped (under-specified) or failed.
 */
export const ensureConversion = async (
  result: Result,
  siteConfig: SiteConfig | undefined,
  correlationId: string,
  flowType?: string,
  location?: EnsureConversionLocation,
  trafficSource?: string,
  monetizationChannel?: string,
): Promise<string | undefined> => {
  const offeringId = result.offeringId?.trim() || result.offeringUuid?.trim();
  const tenantId = siteConfig?.tenantId?.trim();
  if (!offeringId || !tenantId) return undefined;

  // `buildConversionCreationRequest` reads `offeringId` directly; normalize the
  // forward `/match` shape (which may only carry `offeringUuid`) so it resolves.
  const resultForConversion: Result = result.offeringId?.trim()
    ? result
    : { ...result, offeringId };

  try {
    const conversion = buildConversionCreationRequest(
      resultForConversion,
      siteConfig ?? ({} as SiteConfig),
      result.productClickedCorrelationId || correlationId,
      { flowType, trafficSource, monetizationChannel, location },
    );
    const response = await initConversion(conversion);
    return response.conversionId;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '[ensureConversion] click-time conversion create failed; the user still routes into the application view',
      error,
    );
    return undefined;
  }
};
